<script setup lang="ts">
import './assets/style.scss'
import { useRoute } from 'vue-router'
import { useUpdateAcceptStatus } from './../model/api.ts'
import { VIcons, VModal } from '@/shared/ui'
import { useModel } from '@/features/Inventory/AcceptStatusButton/model/model.ts'
import { computed, type PropType, ref } from 'vue'
import { useAuth } from '@/entities/Auth'
import { useTranslate } from '@/shared/lib'

const props = defineProps({
  /** `default`: tonal buttons (list edit). `toolbar`: outlined compact buttons for passport hero. */
  appearance: {
    type: String as PropType<'default' | 'toolbar'>,
    default: 'default',
    validator: (v: string) => v === 'default' || v === 'toolbar',
  },
  status: {
    type: String,
    default: '',
  },
  status_date: {
    type: Object as PropType<string | null>,
    default: '',
  },
  /** Toolbar rendering mode: auto by current status, submit-only for DRAFT/REWORK, review-only for PREACCEPT. */
  toolbarMode: {
    type: String as PropType<'auto' | 'submit' | 'review'>,
    default: 'auto',
    validator: (v: string) => v === 'auto' || v === 'submit' || v === 'review',
  },
})

const { open, statusType, comment, file, clearData, rules } = useModel()
const { isPermissions } = useAuth()
const { trs } = useTranslate()
const $t = trs

const route = useRoute()
const { update, isPending } = useUpdateAcceptStatus()

const emits = defineEmits(['updated'])
const hasStatusDate = computed(
  () => props.status_date != null && String(props.status_date).trim().length > 0,
)
const canShowSubmit = computed(() => {
  if (!(props.status == 'DRAFT' || props.status == 'REWORK')) return false
  if (!isPermissions(['ACCEPT_STATUS_PREACCEPT'])) return false
  if (props.toolbarMode === 'review') return false
  return true
})
const canShowReview = computed(() => {
  if (props.status !== 'PREACCEPT') return false
  if (!isPermissions(['ACCEPT_STATUS_FINISH'])) return false
  if (props.toolbarMode === 'submit') return false
  return true
})

/**
 * Функция обработки клика по кнопке
 * @param nextStatus
 */
const clickHandler = (nextStatus: string) => {
  if (nextStatus === 'ACCEPTED' && !hasStatusDate.value) return
  statusType.value = nextStatus
  open.value = true
}

/**
 * Функция отправки формы
 */
const submitHandler = () => {
  if (statusType.value === 'ACCEPTED' && !hasStatusDate.value) return
  update({
    object_id: route.params.invent_object_id,
    accept_status: statusType.value,
    accept_status_comment: comment.value,
    accept_status_file: file.value,
    date_status: props.status_date,
  }).then(() => {
    emits('updated')
    clearData()
  })
}

const valid = ref(false)
</script>

<template>
  <template v-if="appearance === 'default'">
    <template
      v-if="(status == 'DRAFT' || status == 'REWORK') && isPermissions(['ACCEPT_STATUS_PREACCEPT'])"
    >
      <VBtn variant="tonal" @click.prevent="clickHandler('PREACCEPT')">{{
        $t('itf.btn_preaccepted')
      }}</VBtn>
    </template>

    <template v-if="status == 'PREACCEPT' && isPermissions(['ACCEPT_STATUS_FINISH'])">
      <VBtn variant="tonal" :disabled="!hasStatusDate" @click.prevent="clickHandler('ACCEPTED')">
        {{ $t('itf.btn_accepted') }}
      </VBtn>

      <VBtn variant="tonal" color="error" @click.prevent="clickHandler('CANCELLED')">
        {{ $t('itf.btn_canceled') }}
      </VBtn>

      <VBtn variant="tonal" color="primary" @click.prevent="clickHandler('REWORK')">
        {{ $t('itf.btn_rework') }}
      </VBtn>
    </template>
  </template>

  <template v-else>
    <span v-if="canShowSubmit" class="invent-accept-status-toolbar d-inline-flex align-center">
      <VTooltip location="top">
        <template #activator="{ props: tipProps }">
          <VBtn
            v-bind="tipProps"
            :variant="toolbarMode === 'submit' ? 'flat' : 'outlined'"
            :color="toolbarMode === 'submit' ? 'primary' : undefined"
            density="default"
            size="default"
            class="text-none"
            :height="40"
            :disabled="!hasStatusDate"
            :aria-label="$t('itf.btn_preaccepted')"
            @click.prevent="clickHandler('PREACCEPT')"
          >
            <VIcons type="search_check_2" />
          </VBtn>
        </template>
        <span>{{ hasStatusDate ? $t('itf.btn_preaccepted') : 'Выберите дату версии' }}</span>
      </VTooltip>
    </span>

    <span v-else-if="canShowReview" class="invent-accept-status-toolbar d-inline-flex align-center ga-2">
      <VTooltip location="top">
        <template #activator="{ props: tipProps }">
          <VBtn
            v-bind="tipProps"
            variant="outlined"
            density="default"
            size="default"
            class="text-none text-success"
            :height="40"
            :disabled="!hasStatusDate"
            :aria-label="$t('itf.btn_accepted')"
            @click.prevent="clickHandler('ACCEPTED')"
          >
            <VIcons type="check_circle" />
          </VBtn>
        </template>
        <span>{{ hasStatusDate ? $t('itf.btn_accepted') : 'Выберите дату версии перед согласованием' }}</span>
      </VTooltip>

      <VTooltip location="top">
        <template #activator="{ props: tipProps }">
          <VBtn
            v-bind="tipProps"
            variant="outlined"
            density="default"
            size="default"
            class="text-none text-error"
            :height="40"
            :aria-label="$t('itf.btn_canceled')"
            @click.prevent="clickHandler('CANCELLED')"
          >
            <VIcons type="cancel" />
          </VBtn>
        </template>
        <span>{{ $t('itf.btn_canceled') }}</span>
      </VTooltip>

      <VTooltip location="top">
        <template #activator="{ props: tipProps }">
          <VBtn
            v-bind="tipProps"
            variant="outlined"
            density="default"
            size="default"
            class="text-none text-primary"
            :height="40"
            :aria-label="$t('itf.btn_rework')"
            @click.prevent="clickHandler('REWORK')"
          >
            <VIcons type="assignment_return" />
          </VBtn>
        </template>
        <span>{{ $t('itf.btn_rework') }}</span>
      </VTooltip>
    </span>
  </template>

  <VModal v-model="open" :title="$t('itf.status_accept_confirm')">
    <VForm v-model="valid" class="mt-2" @submit.prevent="submitHandler">
      <VTextarea v-model="comment" :label="$t('itf.comment')" :rules="rules.comment" />

      <VFileInput v-model="file" :label="$t('itf.file')" />

      <div class="form__btn_right">
        <VBtn color="default" variant="tonal" @click="clearData">
          {{ $t('itf.cancel') }}
        </VBtn>

        <VBtn type="submit" :loading="isPending">{{ $t('itf.submit') }}</VBtn>
      </div>
    </VForm>
  </VModal>
</template>
